import type { Rotation } from './orientation.js';

/** Rooms per edge of a match cube (10 × 10 × 10 = 1,000 rooms). */
export const CUBE_SIZE = 10;

/**
 * Lock lengths in milliseconds. Every action plays a fixed animation and input is
 * locked until it finishes; these are the brief's starting values, to be tuned in playtesting.
 */
export const ACTION_MS = {
  step: 400,
  turn: 250,
  pitch: 300,
  roll: 250,
  bonk: 250,
  swap: 800,
  /** Firing the EMP or slamming a bulkhead. */
  useGear: 300,
} as const;

/** Tactical gear: every match starts with one of each; caches hold more. */
export const GEAR = {
  startingEmp: 1,
  startingBulkhead: 1,
  carryLimit: 2,
  cacheCount: 6,
  /** EMP blinds rival sensors within this straight-line radius (rooms)… */
  empRadius: 2.0,
  /** …for this long (ms). */
  empMs: 5000,
  /** A bulkhead seals the door just passed, from both sides, for this long (ms). */
  bulkheadMs: 8000,
} as const;

/** Thruster scorch left in a room when an agent leaves it (1v1 and CTF values). */
export const SCORCH = {
  /** The streak shows which door they left by for this long… */
  directionMs: 5000,
  /** …and the mark disappears entirely after this long. */
  fadeMs: 30000,
} as const;

export function rotationMs(rotation: Rotation): number {
  if (rotation === 'pitchUp' || rotation === 'pitchDown') return ACTION_MS.pitch;
  if (rotation === 'rollLeft' || rotation === 'rollRight') return ACTION_MS.roll;
  return ACTION_MS.turn;
}

/** Sensor radii in rooms, measured in a straight line that ignores walls. */
export const THREAT_RADIUS = 2.5;
export const CORE_RADIUS = 4.0;

/** Rooms visible through open doorways before the haze swallows them. */
export const SIGHTLINE_ROOMS = 3;

/** Maze generation: chance that a non-tree wall gets a door anyway, creating loops. */
export const LOOP_CHANCE = 0.07;
/** A few loop doors are one-way: passable from one side only. */
export const ONE_WAY_DOORS = 12;

/** Spawn placement: both players start this many doors from the core (equal for both). */
export const SPAWN_PATH_MIN = 14;
export const SPAWN_PATH_MAX = 20;
/** …and at least this many doors from each other. */
export const SPAWN_SEPARATION_MIN = 10;
